import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Place a scheduled structure into the yard.
 *
 * @intrasync/yard-management has no concept of a production schedule or a sales
 * order, and no host extension point in its hub (YardLayoutModule takes only
 * rootStore/context/initialMode/initialTabId, and YARD_HUB_ACTIONS is a fixed
 * const, not a registry). So this lives beside the module as a host screen
 * rather than inside it, and writes into the module's own tables.
 *
 * The join is the one the retired NTXYardMap performed:
 *
 *   production_schedules -> sales_order_lines -> sales_orders -> products
 *
 * with `structureRef` parsed out of the order line's notes, exactly as before.
 *
 * Placing writes three rows:
 *   yard_assets            the thing in the yard
 *   yard_asset_placements  where it sits (a layout block)
 *   ntx_yard_asset_links   which production run it came off
 */

// Service stores are loosely typed at the host boundary; the module's rows carry
// their own shapes and we only touch a documented subset of each.
type AnyStore = any; // eslint-disable-line @typescript-eslint/no-explicit-any
type AnyRow = Record<string, unknown>;

export interface PlaceableStructure {
  scheduleId: string;
  productId: string;
  productName: string;
  displayName: string;
  structureRef?: string;
  salesOrderLineId?: string;
  orderNumber?: string;
  jobName?: string;
  quantity: number;
  /** True once this structure already has an asset in the yard. */
  alreadyPlaced: boolean;
}

export interface DestinationBlock {
  id: string;
  name: string;
  capacity: number | null;
  occupied: number;
  /** occupied / capacity as 0–1, or null when capacity is untracked. */
  utilization: number | null;
  /** Footprint as the module stores it. Metric on the way in and out of the
   *  database; converted only at render time. See shared/yardUnits. */
  lengthMm: number | null;
  widthMm: number | null;
}

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

/** Uppercase A–Z0–9 slug, matching the code shape the data migration wrote. */
const slug = (s: string, max = 32): string =>
  s
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, max);

class PlaceFromScheduleStore {
  rootStore: RootStore;

  searchQuery = "";
  selectedBlockId: string | null = null;
  /** scheduleId currently being written, so the row can show progress. */
  placingScheduleId: string | null = null;
  loading = false;
  error: string | null = null;
  lastPlaced: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ── Service store accessors ──
  // Host side
  private get _scheduleStore(): AnyStore {
    return (this.rootStore as AnyStore).productionSchedulesStore;
  }
  private get _productStore(): AnyStore {
    return (this.rootStore as AnyStore).productsStore;
  }
  private get _orderStore(): AnyStore {
    return (this.rootStore as AnyStore).salesOrdersStore;
  }
  private get _orderLineStore(): AnyStore {
    return (this.rootStore as AnyStore).salesOrderLinesStore;
  }
  // Module side
  private get _blockStore(): AnyStore {
    return (this.rootStore as AnyStore).yardGridLayoutBlocksStore;
  }
  private get _assetStore(): AnyStore {
    return (this.rootStore as AnyStore).yardAssetsStore;
  }
  private get _assetTypeStore(): AnyStore {
    return (this.rootStore as AnyStore).yardAssetTypesStore;
  }
  private get _placementStore(): AnyStore {
    return (this.rootStore as AnyStore).yardAssetPlacementsStore;
  }
  // Bridge
  private get _linkStore(): AnyStore {
    return (this.rootStore as AnyStore).ntxYardAssetLinksStore;
  }

  private entities(store: AnyStore): AnyRow[] {
    return (store?.entities as AnyRow[]) || [];
  }

  // ── Lookups (same resolution the old store used) ──

  private getProduct(productId?: unknown): AnyRow | undefined {
    if (!productId) return undefined;
    return this.entities(this._productStore).find((p) => p.id === productId);
  }

  private getLine(lineId?: unknown): AnyRow | undefined {
    if (!lineId) return undefined;
    return this.entities(this._orderLineStore).find(
      (l) => (l.id ?? l.salesOrderLineId) === lineId,
    );
  }

  private getOrder(orderId?: unknown): AnyRow | undefined {
    if (!orderId) return undefined;
    return this.entities(this._orderStore).find(
      (o) => (o.id ?? o.salesOrderId) === orderId,
    );
  }

  /** Structure number (e.g. "SN 004") parsed from the linked order line's notes. */
  private structureRefFromLine(lineId?: unknown): string | undefined {
    const notes = str(this.getLine(lineId)?.notes);
    const match = notes?.match(/SN\s+[^·]+/i);
    return match ? match[0].trim() : undefined;
  }

  // ── Already-placed set ──

  /**
   * Both keys matter. Links written by this screen carry a productionScheduleId;
   * the 17 rows carried over from the retired yard do not — they were projected
   * by the B.10 seed rather than placed through a UI, so structureRef is all
   * they have. Keying on either avoids offering a structure that is already out
   * there under its ref.
   */
  private get placedKeys(): { schedules: Set<string>; refs: Set<string> } {
    const schedules = new Set<string>();
    const refs = new Set<string>();
    for (const l of this.entities(this._linkStore)) {
      const s = str(l.productionScheduleId);
      if (s) schedules.add(s);
      const r = str(l.structureRef);
      if (r) refs.add(r);
    }
    return { schedules, refs };
  }

  // ── Placeable structures ──

  get placeableStructures(): PlaceableStructure[] {
    const { schedules, refs } = this.placedKeys;
    const q = this.searchQuery.trim().toLowerCase();

    return this.entities(this._scheduleStore)
      .filter((si) => si.status !== "cancelled")
      .map((si): PlaceableStructure => {
        const product = this.getProduct(si.productId);
        const line = this.getLine(si.salesOrderLineId);
        const order = line ? this.getOrder(line.salesOrderId) : undefined;
        const structureRef = this.structureRefFromLine(si.salesOrderLineId);
        const scheduleId = String(si.id);
        return {
          scheduleId,
          productId: String(si.productId ?? ""),
          productName: str(product?.productName) || String(si.productId ?? ""),
          displayName:
            str(product?.productName) ||
            str(product?.productCode) ||
            String(si.productId ?? ""),
          structureRef,
          salesOrderLineId: str(si.salesOrderLineId),
          orderNumber: str(order?.orderNumber),
          jobName: str(order?.jobName),
          quantity: typeof si.quantity === "number" ? si.quantity : 1,
          alreadyPlaced:
            schedules.has(scheduleId) ||
            Boolean(structureRef && refs.has(structureRef)),
        };
      })
      .filter((s) => {
        if (!q) return true;
        return [s.displayName, s.productName, s.structureRef, s.orderNumber, s.jobName]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (a.alreadyPlaced !== b.alreadyPlaced) return a.alreadyPlaced ? 1 : -1;
        return (a.structureRef || a.displayName).localeCompare(
          b.structureRef || b.displayName,
        );
      });
  }

  get unplacedCount(): number {
    return this.placeableStructures.filter((s) => !s.alreadyPlaced).length;
  }

  // ── Destination blocks ──

  get destinationBlocks(): DestinationBlock[] {
    const placements = this.entities(this._placementStore);
    return this.entities(this._blockStore)
      .filter((b) => b.status !== "inactive")
      .map((b): DestinationBlock => {
        const occupied = placements.filter((p) => p.blockId === b.id).length;
        const capacity =
          typeof b.capacity === "number" && b.capacity > 0 ? b.capacity : null;
        const num = (v: unknown): number | null =>
          typeof v === "number" && Number.isFinite(v) ? v : null;
        return {
          id: String(b.id),
          name: str(b.name) || String(b.id),
          capacity,
          occupied,
          utilization: capacity ? occupied / capacity : null,
          lengthMm: num(b.lengthMm),
          widthMm: num(b.widthMm),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get selectedBlock(): DestinationBlock | null {
    return this.destinationBlocks.find((b) => b.id === this.selectedBlockId) || null;
  }

  // ── Actions ──

  setSearchQuery = (q: string) => {
    this.searchQuery = q;
  };

  setSelectedBlock = (id: string | null) => {
    this.selectedBlockId = id;
  };

  /**
   * Resolve the module asset type for a product, creating it if this product has
   * never been placed. Codes follow the same slug rule the data migration used,
   * so a product carried over from the legacy yard resolves to its existing type
   * rather than gaining a duplicate.
   */
  private async resolveAssetTypeId(structure: PlaceableStructure): Promise<string> {
    const product = this.getProduct(structure.productId);
    const code = slug(
      str(product?.productCode) || str(product?.code) || structure.productId.slice(0, 8),
    );

    const existing = this.entities(this._assetTypeStore).find((t) => t.code === code);
    if (existing) return String(existing.id);

    const created = await this._assetTypeStore.create({
      code,
      name: structure.productName || "Precast structure",
      category: "precast",
      stackable: false,
      hazmat: false,
      isActive: true,
    });
    return String((created as AnyRow).id);
  }

  /**
   * `companyId` is never sent to the module's services. It is one of their
   * SERVER_FIELDS — stripped from input (posting it fails validation outright
   * with "must NOT have additional properties") and resolved server-side from
   * the caller's user. The link row is ours, so it carries companyId normally.
   *
   * Write the three rows. Not a transaction — these are three Feathers services
   * over HTTP, so a failure partway leaves an asset with no placement or no
   * link. That is recoverable and visible (the module's own Unassigned Assets
   * dialog surfaces exactly this case) and is preferred to inventing a custom
   * batch endpoint on the module's tables.
   */
  placeStructure = async (
    structure: PlaceableStructure,
    blockId: string,
  ): Promise<void> => {
    runInAction(() => {
      this.placingScheduleId = structure.scheduleId;
      this.error = null;
    });

    try {
      const assetTypeId = await this.resolveAssetTypeId(structure);

      const asset = (await this._assetStore.create({
        assetTypeId,
        tag: structure.structureRef || `SCH-${structure.scheduleId.slice(0, 8)}`,
        description: structure.jobName ?? null,
        status: "in_yard",
        hazmat: false,
        // pieceId deliberately left NULL — NTX has no @intrasync/core, and the
        // module reserves it for core `pieces`. The origin lives on the link row.
      })) as AnyRow;

      await this._placementStore.create({
        assetId: asset.id,
        blockId,
      });

      await this._linkStore.create({
        yardAssetId: asset.id,
        productionScheduleId: structure.scheduleId,
        salesOrderLineId: structure.salesOrderLineId ?? null,
        structureRef: structure.structureRef ?? null,
        companyId: "NTX",
      });

      runInAction(() => {
        this.lastPlaced = structure.structureRef || structure.displayName;
      });
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to place structure.";
      });
      console.error("[NTX Yard] placeStructure failed:", err);
      throw err;
    } finally {
      runInAction(() => {
        this.placingScheduleId = null;
      });
    }
  };

  loadData = async (): Promise<void> => {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    const calls: Array<[string, Promise<unknown> | undefined]> = [
      ["production-schedules", this._scheduleStore?.find({ query: { $limit: 2000 } })],
      ["products", this._productStore?.find({ query: { $limit: 2000 } })],
      ["sales-orders", this._orderStore?.find({ query: { $limit: 500 } })],
      ["sales-order-lines", this._orderLineStore?.find({ query: { $limit: 2000 } })],
      ["yard-grid-layout-blocks", this._blockStore?.find({ query: { $limit: 500 } })],
      ["yard-asset-types", this._assetTypeStore?.find({ query: { $limit: 500 } })],
      ["yard-asset-placements", this._placementStore?.find({ query: { $limit: 2000 } })],
      ["ntx-yard-asset-links", this._linkStore?.find({ query: { $limit: 2000 } })],
    ];

    const results = await Promise.allSettled(
      calls.map(([, p]) => p).filter(Boolean) as Array<Promise<unknown>>,
    );
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        console.warn(`[NTX Yard] ${calls[i][0]} find() failed:`, r.reason);
      }
    });

    runInAction(() => {
      this.loading = false;
    });
  };
}

export default PlaceFromScheduleStore;
